import { fetchGet } from "..";
import { Race } from "../../types/races/race";
import { APIResponse, Meta } from "../../types/responses/APIResponse";
import { Meeting } from "../../types/races/meeting";
import {
  Runner,
  RunnerPromptData,
  InsightsHorse,
  InsightsMetric,
} from "../../types/races/runner";
import { prepareParams } from "../../components/Screens/Results/utils";
import {
  isParamFriendlyDateFormat,
  isParamFriendlyTimeFormat,
} from "../../utils/validParams";
import { SmartStats } from "../../types/races/smartStats";
import { Asset } from "../../types/asset";

type VideoRacePreview = {
  sources: {
    url: string;
    content_type: string;
    video_codec: string;
    audio_codec: string;
  }[];
};

export type RaceData = {
  race: Race;
  runners: Runner[];
  smart_stats: SmartStats;
  last_winners: Runner[];
  series: {
    name: string;
    image: Asset;
  };
};

export type PromptsResponse = {
  meta: Meta;
  runners: RunnerPromptData[];
};

export const getRace = async (
  date: string,
  track: string,
  time: string
): Promise<APIResponse<RaceData>> => {
  const { date: preparedDate, time: preparedTime } = prepareParams({
    date: date,
    time: time,
  });

  if (
    isParamFriendlyDateFormat(preparedDate) &&
    isParamFriendlyTimeFormat(preparedTime)
  )
    return await fetchGet(
      `racing/racecards/${preparedDate}/${track}/${preparedTime}`
    );
  else throw new Error("Invalid date or time format");
};

// Racecards API
export type RacecardResponse = {
  meta: Meta;
  meetings: Meeting[];
};

export const getRacecardsByDate = async (
  date?: string
): Promise<RacecardResponse> => {
  if (isParamFriendlyDateFormat(date))
    return await fetchGet(`racing/racecards/list/${date}`); // yyyy-mm-dd
  else throw new Error("Invalid date format");
};

// NonRunners API
export const getNonRunners = async (): Promise<APIResponse<Meeting[]>> => {
  return await fetchGet(`racing/racecards/non-runners`);
};
// export const cleanSlug = (slug: string) => {
//   if (slug.startsWith("/")) slug = slug.substring(1); // remove first /
//   return slug;
// };

// QLS Race Previews API

export const getRacePreview = async (
  date: string,
  track: string,
  time: string
): Promise<APIResponse<Race>> => {
  if (
    // (isToday(date) || isTomorrow(date)) && // this breaks for americas because date
    // is taken from start of day in UTC, e.g. 00:00
    // then converted to local time, making it day before.
    isParamFriendlyDateFormat(date) &&
    isParamFriendlyTimeFormat(time)
  )
    return await fetchGet(`racing/racecards/${date}/${track}/${time}/preview`);
  else throw new Error("Invalid date or time format");
};

export const getVideoRacePreview = async ({
  date,
  track,
  race,
  type,
  prerollToken,
}: {
  date: string;
  track: string;
  race: number;
  type: "form-analysis" | "predictor";
  prerollToken?: string;
}): Promise<APIResponse<VideoRacePreview>> => {
  return await fetchGet(
    `member/watch/previews/${date}/${track}/${race}/${type}`,
    prerollToken ? { preroll_token: prerollToken } : {}
  );
};

export const getRaceTips = async (
  date: string,
  track: string,
  time: string
) => {
  if (isParamFriendlyDateFormat(date) && isParamFriendlyTimeFormat(time))
    return await fetchGet(`racing/racecards/${date}/${track}/${time}/tips`);
  else throw new Error("Invalid date or time format");
};

type RaceIqInsightResponse = {
  horses?: InsightsHorse[];
  metrics?: InsightsMetric[];
};

export const getRaceIqInsights = async (
  date: string,
  track: string,
  time: string,
  type: "horses" | "metrics"
): Promise<APIResponse<RaceIqInsightResponse>> => {
  if (isParamFriendlyDateFormat(date) && isParamFriendlyTimeFormat(time)) {
    try {
      const { date: preparedDate, time: preparedTime } = prepareParams({
        date: date,
        time: time,
      });

      const data = await fetchGet(
        `racing/iq-races/${preparedDate}/${track}/${preparedTime}/insights/${type}`
      );

      return data;
    } catch (error) {
      console.log(`Error fetching RaceiQ insights ${type}:`, error);
      return undefined;
    }
  }
};

export const getRaceIqPrompts = async (
  date: string,
  track: string,
  time: string
): Promise<APIResponse<PromptsResponse>> => {
  try {
    const { date: preparedDate, time: preparedTime } = prepareParams({
      date: date,
      time: time,
    });

    if (
      isParamFriendlyDateFormat(preparedDate) &&
      isParamFriendlyTimeFormat(preparedTime)
    ) {
      const res = await fetchGet(
        `racing/iq-races/${preparedDate}/${track}/${preparedTime}/prompts`
      );

      if ("error" in res || !res.runners)
        throw new Error("error fetching prompts");

      return res as APIResponse<PromptsResponse>;
    }
  } catch (error) {
    console.error("Error", error);
    return undefined;
  }
};
